import { ISortByQuery } from '@common/api.models';
import { DEFAULT_TABLE_PAGE_SIZE_OPTIONS } from '@common/constants';
import { SearchFilter } from '@common/searchFilter/SearchFilter';
import { IFilterFieldConfig, SearchFilterType } from '@common/searchFilter/searchFilter.models';
import { SearchPageBase } from '@common/SearchPageBase';
import {
  createTableDefaultColumn,
  createTableLinkWithOpenNewTabColumn,
  createTableUserRoleColumn,
} from '@common/tableColumn.services';
import { Column, ComponentType, Icon, isNullOrWhiteSpace, ResponsivePaginatedTable } from '@gcl/react-core';
import { routes } from '@routes/routes.models';
import { useFetchDesignerCompanyOptions } from '@company/projectCompany/projectCompany.queries';
import { useFetchSrpOptionsWithSearch, useFetchTwcOptionsWithSearch } from '@userProfile/userProfile.queries';
import * as React from 'react';
import { useFetchDesignBriefSearch } from './designBrief.queries';
import {
  EDesignBriefSearchColumnKeys,
  EDesignBriefSearchKey,
  IDesignBriefSearch,
  IDesignBriefSearchFilter,
} from './designBrief.models';
import { designBriefActionerOptions } from './designBriefOptions.models';
import './DesignBriefList.scss';
import { useProjectId } from '@config/config.hooks';
import { useNavigate } from '@routes/routes.hooks';
import { IButtonNavBarItem } from '@app/ButtonNavBar';
import { useSearchFilterUrlParam } from '@common/searchFilter/searchFilterUrlParam.hooks';
import { NumberParam, StringParam } from 'use-query-params';
import { getSelectFilterFieldNameId } from '@common/searchFilter/searchFilter.services';
import { DownloadButton } from '@file/DownloadButton';
import { EUserProfilePreferenceAttributeName } from '@userProfile/userProfilePreferences.constants';
import { useUserProfilePreferencesPageSize } from '@userProfile/userProfilePreferences.hooks';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import {
  exportProjectDesignBriefSearchExcelAsync,
  exportProjectDesignBriefSearchPdfAsync,
} from './designBrief.services';
import { useAllowNewT2 } from './designBrief.hooks';
import { useDesignBriefCreationModal } from './designBriefCreationModal.hooks';
import { EDesignBriefStatus } from './designBrief.constants';
import '@common/Common.scss';

export interface IDesignBriefListProps {}

export const DesignBriefList: React.FunctionComponent<IDesignBriefListProps> = ({}) => {
  const { initPageSize, pageSize, setPageSize } = useUserProfilePreferencesPageSize(
    EUserProfilePreferenceAttributeName.T2_LIST_PAGE_SIZE,
  );
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [sortByQuery, setSortByQuery] = React.useState<ISortByQuery | undefined>({
    sortByField: EDesignBriefSearchColumnKeys.designBriefNo,
    isDesc: true,
  });
  const projectId = useProjectId();
  const { navigate, generateRoutePath, projectNo } = useNavigate();

  // #region Search Field Configs
  const designBriefSearchFieldConfigs = React.useMemo(() => {
    return [
      {
        fieldName: EDesignBriefSearchKey.designBriefNo,
        displayName: 'T2 No.',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      {
        fieldName: EDesignBriefSearchKey.t1RefNo,
        displayName: 'T1',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,

      {
        fieldName: EDesignBriefSearchKey.designPackage,
        displayName: 'Design Package',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      {
        fieldName: EDesignBriefSearchKey.description,
        displayName: 'Description',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      {
        fieldName: EDesignBriefSearchKey.riskCategory,
        displayName: 'Risk Category',
        type: SearchFilterType.TextSelect,
        textSelectOptions: ['A', 'B', 'C', 'D'],
      } as IFilterFieldConfig,

      {
        fieldName: EDesignBriefSearchKey.actioner,
        displayName: 'Actioner',
        type: SearchFilterType.TextSelect,
        textSelectOptions: designBriefActionerOptions,
      } as IFilterFieldConfig,
      {
        fieldName: EDesignBriefSearchKey.srp,
        displayName: 'SRP',
        type: SearchFilterType.SelectWithSearch,
        useFetchOptionsWithSearch: useFetchSrpOptionsWithSearch({ shouldConvertOptions: true }),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: EDesignBriefSearchKey.twc,
        displayName: 'TWC',
        type: SearchFilterType.SelectWithSearch,
        useFetchOptionsWithSearch: useFetchTwcOptionsWithSearch({ shouldConvertOptions: true }),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: EDesignBriefSearchKey.designer,
        displayName: 'Designer',
        type: SearchFilterType.Select,
        useFetchOptions: useFetchDesignerCompanyOptions({}),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
    ];
  }, []);
  // #endregion Search Field Configs

  const { localFilter, setLocalFilter } = useSearchFilterUrlParam(
    {
      [EDesignBriefSearchKey.designBriefNo]: StringParam,
      [EDesignBriefSearchKey.description]: StringParam,
      [EDesignBriefSearchKey.t1RefNo]: StringParam,
      [EDesignBriefSearchKey.designPackage]: StringParam,
      [EDesignBriefSearchKey.actioner]: StringParam,
      [`${getSelectFilterFieldNameId(EDesignBriefSearchKey.riskCategory)}`]: NumberParam,
      [`${getSelectFilterFieldNameId(EDesignBriefSearchKey.designer)}`]: NumberParam,
      [EDesignBriefSearchKey.twc]: StringParam,
      [EDesignBriefSearchKey.srp]: StringParam,
    },
    designBriefSearchFieldConfigs,
  );

  const designBriefFilterObj = React.useMemo(() => {
    const temp = {} as any;

    Object.keys(localFilter).forEach((key) => {
      if (!key.includes('.')) temp[key] = localFilter[key];
      else {
        const splitedKey = key.split('.');
        const child = localFilter[splitedKey[0]] ?? {};
        child[splitedKey[1]] = localFilter[key];
        temp[splitedKey[0]] = child;
      }
    });

    return temp as IDesignBriefSearchFilter;
  }, [JSON.stringify(localFilter)]);

  const {
    data: designBriefPagedResult,
    isLoading: isLoadingDesignBriefSearch,
    isRefetching: isRefetchingDesignBriefSearch,
    error: errorDesignBriefSearch,
  } = useFetchDesignBriefSearch({
    pageSize,
    pageNumber,
    projectId,
    ...(sortByQuery || {}),
    filter: Object.keys(localFilter).length > 0 ? (localFilter as IDesignBriefSearchFilter) : undefined,
  });

  const {
    renderModal: renderDesignBriefCreationModal,
    openPortal: openDesignBriefCreationModal,
    closePortal: closeDesignBriefCreationModal,
  } = useDesignBriefCreationModal({ projectId });

  // #region Table Columns
  const designBriefTableColumns: Column[] = React.useMemo(() => {
    return [
      createTableLinkWithOpenNewTabColumn<IDesignBriefSearch>(
        EDesignBriefSearchColumnKeys.designBriefNo,
        'T2 No.',
        ({ item }) => generateRoutePath(routes.t2.view.path, { id: item.id.toString() }),
        ({ value, item, index, cellExtras }, link) => {
          let statusIcon;
          if (item.status === EDesignBriefStatus.CANCELLED || item.projectMasterSchedule.isCancelled)
            statusIcon = <Icon iconName="X" className="gcl-gems-design-brief-list-not-released-icon gcl-ml-xs" />;
          else if (item.status === EDesignBriefStatus.FINISHED)
            statusIcon = (
              <Icon iconName="CheckCircleFill" className="gcl-gems-design-brief-list-released-icon gcl-ml-xs" />
            );
          else
            statusIcon = (
              <Icon iconName="RecordCircle" className="gcl-gems-design-brief-list-not-released-icon gcl-ml-xs" />
            );

          return (
            <>
              {link}
              {statusIcon}
            </>
          );
        },
      ),
      createTableDefaultColumn<IDesignBriefSearch>(
        EDesignBriefSearchColumnKeys.actioner,
        'Actioner',
        undefined,
        ({ item }) => {
          if (!isNullOrWhiteSpace(item.actioner)) {
            if (item.actioner?.trim() === '-') return 'gcl-gems-design-brief-cell no-action';
            return `gcl-gems-design-brief-cell ${item.actioner?.toLowerCase()}`;
          }

          return '';
        },
      ),
      createTableLinkWithOpenNewTabColumn<IDesignBriefSearch>(
        EDesignBriefSearchColumnKeys.t1RefNo,
        'T1',
        ({ item }) => generateRoutePath(routes.t1.view.path, { id: item.projectMasterSchedule.id?.toString() }),
        ({ value, item, index, cellExtras }, link) => link,
      ),
      createTableDefaultColumn(EDesignBriefSearchColumnKeys.designPackage, 'Design Package'),
      createTableDefaultColumn(EDesignBriefSearchColumnKeys.designer, 'Designer'),
      createTableUserRoleColumn(EDesignBriefSearchColumnKeys.twc, 'TWC'),
      createTableUserRoleColumn(EDesignBriefSearchColumnKeys.srp, 'SRP'),
      createTableDefaultColumn(EDesignBriefSearchColumnKeys.riskCategory, 'Risk Cat.'),
    ];
  }, []);
  // #endregion Table Columns

  const allowNewT2 = useAllowNewT2();

  return (
    <>
      <ApiErrorDisplay errors={[{ error: errorDesignBriefSearch, genericError: 'Failed to get design brief list' }]} />
      <SearchPageBase
        data={designBriefPagedResult?.items}
        breadcrumbItems={[
          {
            text: `${projectNo} - Home`,
            prefixIconName: 'House',
            onClick: () => {
              navigate(routes.t1.path);
            },
          },
          {
            text: `Search T2`,
            prefixIconName: 'Search',
            active: true,
          },
        ]}
        title="T2 Design Brief List"
        isTableRefetching={isRefetchingDesignBriefSearch}
        isTableLoading={isLoadingDesignBriefSearch}
        buttonNavBarItem={[
          ...(allowNewT2
            ? [
                {
                  text: 'New',
                  onClick: (e: React.MouseEvent) => {
                    openDesignBriefCreationModal(e);
                  },
                  iconName: 'FileEarmarkPlus',
                  componentType: ComponentType.Primary,
                  showNoneInArchivedProject: true,
                } as IButtonNavBarItem,
              ]
            : ([] as unknown as IButtonNavBarItem[])),
          {
            text: 'Export',
            iconName: 'ArrowBarDown',
            componentType: ComponentType.Secondary,
            component: (
              <DownloadButton
                exportFunction={exportProjectDesignBriefSearchExcelAsync}
                defaultExportProps={{
                  projectId,
                  sortByQuery,
                  searchFilter: designBriefFilterObj,
                }}
              />
            ),
          },
          {
            text: 'Print',
            iconName: 'Printer',
            componentType: ComponentType.Secondary,
            component: (
              <DownloadButton
                text="Print"
                prefixIconName="Printer"
                exportFunction={exportProjectDesignBriefSearchPdfAsync}
                defaultExportProps={{
                  projectId,
                  sortByQuery,
                  searchFilter: designBriefFilterObj,
                }}
              />
            ),
          },
        ]}
        searchFilter={
          <SearchFilter
            fieldConfigs={designBriefSearchFieldConfigs}
            onFilterChanged={(filter) => {
              setLocalFilter(filter);
            }}
            defaultFilter={localFilter}
          />
        }
        appliedSearchFilter={localFilter}
        noContentLabel="No T2 created yet"
        onRenderTable={() => {
          return (
            <>
              <ResponsivePaginatedTable
                classNames={{ body: 'gcl-gems-list-table-body' }}
                columns={designBriefTableColumns}
                paginationProps={{
                  pageSizeOptions: DEFAULT_TABLE_PAGE_SIZE_OPTIONS,
                  paginatorPosition: 'top',
                  totalCount: designBriefPagedResult?.totalCount || 0,
                  initialPageSize: initPageSize,
                  onPageChange: async (pageIndex: number, newPageSize: number) => {
                    setPageNumber(pageIndex + 1);
                    setPageSize(newPageSize);
                  },
                }}
                sortingProps={{
                  initialSortBy:
                    sortByQuery && sortByQuery.sortByField
                      ? [
                          {
                            id: sortByQuery.sortByField,
                            desc: !!sortByQuery.isDesc,
                          },
                        ]
                      : [],
                  onSort: (sortBy) => {
                    if (sortBy && sortBy.length >= 1)
                      setSortByQuery({ sortByField: sortBy[0].id, isDesc: sortBy[0].desc });
                    else setSortByQuery(undefined);
                  },
                }}
                allowSelectRow={false}
                items={designBriefPagedResult?.items || []}
                allowExpandRow={false}
              />
            </>
          );
        }}
        legend={
          <ul>
            <li>
              <Icon iconName="RecordCircle" className="gcl-gems-design-brief-list-not-released-icon" /> - Incomplete
              Form
            </li>
            <li>
              <Icon iconName="CheckCircleFill" className="gcl-gems-design-brief-list-released-icon" /> - Completed Form
            </li>
            <li>
              <Icon iconName="X" className="gcl-gems-design-brief-list-not-released-icon" /> - Cancelled Form
            </li>
          </ul>
        }
      />
      {renderDesignBriefCreationModal()}
    </>
  );
};
